/**
 * designerUtils.ts — Pure utility functions for designer nodes
 *
 * Extracted from the shared ~90% pattern across CharacterDesignerNode,
 * LocationDesignerNode, and ObjectDesignerNode.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import type React from 'react'
import type { AgentColorConfig } from '../../../../constants/agentColors'
import type { LocationAsset, NodeStatus } from '@ponpon/api-client'

/**
 * Resolve the currently-picked image path from a designer output object.
 *
 * Reads `outputObj.images.main.history[picked_index].result.local_path`.
 */
export function resolveDesignerImagePath(
  outputObj: LocationAsset | undefined,
  pickedVariantIndex?: number,
): string | undefined {
  const main = outputObj?.images?.main
  if (main?.history?.length) {
    const idx = pickedVariantIndex ?? main.picked_index ?? 0
    return main.history[idx]?.result?.local_path
  }
  return undefined
}

/**
 * Border color state machine for designer nodes.
 *
 * Priority: selected > running > hovered > default
 *
 * Returns a React.CSSProperties object with borderColor and optional boxShadow.
 */
export function computeBorderStyle(
  selected: boolean,
  status: NodeStatus,
  isHovered: boolean,
  agentColor: AgentColorConfig
): React.CSSProperties {
  if (selected) {
    return { borderColor: agentColor.active, boxShadow: agentColor.glow }
  }
  if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') {
    return { borderColor: agentColor.active }
  }
  if (isHovered) {
    return { borderColor: agentColor.focus }
  }
  return { borderColor: agentColor.hover }
}

/** @deprecated Use `LocationAsset` from `generated/nodeDataTypes` instead */
export type DesignerImageOutput = LocationAsset
